import { useEffect, useState } from 'react'

import { useFetch } from '@reveni-io/reveni-ui'

import { issueRefundAtStatusValues } from '@/common/constants'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'
import {
  getFirstErrorField,
  getRefundMethodFieldId,
  getRefundMethodsConfiguration,
  getRefundMethodsErrors,
  getRefundMethodsErrorsAfterChange,
  getRefundMethodsErrorsFromResponse,
} from '@/configuration/pages/ConfigurationPage/RefundsConfiguration/helpers'
import { getRefundMethodFormValues } from '@/configuration/pages/ConfigurationPage/RefundsConfiguration/RefundMethod/RefundMethodContent/helpers'

import { servicesByConfigurationMode } from './constants'
import { transformRequestRefundMethodsConfiguration } from './transformers'

const useRefundsConfiguration = ({ merchantId, returnPortalId, configurationMode, languages }) => {
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })

  const {
    data,
    loading,
    fetchData: refetchStorePreferences,
  } = useFetch(services.getPreferences, {
    doInitialRequest: true,
    params: entityId,
  })

  const {
    data: allowedRefundMethods,
    loading: loadingAllowedRefundMethods,
    fetchData: refetchAllowedRefundMethods,
  } = useFetch(services.getRefundMethods, {
    doInitialRequest: true,
    params: entityId,
    transformResponse: services.transformResponseRefundMethods,
  })

  const { fetchData: saveConfiguration, loading: savingConfiguration } = useFetch(services.saveConfiguration, {
    doInitialRequest: false,
    transformRequest: transformRequestRefundMethodsConfiguration,
  })

  const [hasChanges, setHasChanges] = useState(false)
  const [issueRefundAtStatus, setIssueRefundAtStatus] = useState(null)
  const [refundMethodsSortable, setRefundMethodsSortable] = useState([])
  const [dragging, setDragging] = useState(false)
  const [currentMode, setCurrentMode] = useState(configurationMode)
  const [expandedRefundMethodIds, setExpandedRefundMethodIds] = useState([])
  const [appliedDraftSource, setAppliedDraftSource] = useState([])
  const [errors, setErrors] = useState({})
  const [listErrors, setListErrors] = useState([])
  const [fieldIdToFocus, setFieldIdToFocus] = useState(null)

  const savedRefundMethods = allowedRefundMethods?.refund_methods

  const setInitValues = () => {
    setIssueRefundAtStatus(
      data.issue_refund_at_status === null ? issueRefundAtStatusValues.manual : data.issue_refund_at_status
    )
    setRefundMethodsSortable(savedRefundMethods.map(refundMethod => getRefundMethodFormValues(refundMethod, languages)))
    setErrors({})
    setListErrors([])
    setHasChanges(false)
  }

  const draftSource = [allowedRefundMethods, data, languages]
  const isDraftStale = draftSource.some((value, index) => value !== appliedDraftSource[index])

  if (allowedRefundMethods && data && isDraftStale) {
    setAppliedDraftSource(draftSource)
    setInitValues()
  }

  useEffect(() => {
    if (currentMode && configurationMode !== currentMode) {
      refetchStorePreferences(entityId)
      refetchAllowedRefundMethods(entityId)
    }
    setCurrentMode(configurationMode)
  }, [configurationMode, currentMode, entityId, refetchStorePreferences, refetchAllowedRefundMethods])

  const revertChanges = () => {
    setInitValues()
  }

  const updateRefundMethod = (refundMethodId, changes) => {
    const updatedRefundMethods = refundMethodsSortable.map(refundMethod => {
      return refundMethod.id === refundMethodId ? { ...refundMethod, ...changes } : refundMethod
    })
    const updatedRefundMethod = updatedRefundMethods.find(refundMethod => refundMethod.id === refundMethodId)

    setHasChanges(true)
    setRefundMethodsSortable(updatedRefundMethods)
    setErrors(current => getRefundMethodsErrorsAfterChange(current, updatedRefundMethod, languages))
    setListErrors([])
  }

  const showErrors = (refundMethodsErrors, newListErrors = []) => {
    const firstErrorField = getFirstErrorField({
      refundMethods: refundMethodsSortable,
      errors: refundMethodsErrors,
      languages,
    })

    setErrors(refundMethodsErrors)
    setListErrors(newListErrors)

    if (!firstErrorField) return

    setExpandedRefundMethodIds(ids => {
      return ids.includes(firstErrorField.refundMethodId) ? ids : [...ids, firstErrorField.refundMethodId]
    })
    setFieldIdToFocus(getRefundMethodFieldId(firstErrorField.refundMethodName, firstErrorField.field))
  }

  const saveRefundMethods = async () => {
    if (!hasChanges) return true

    const validationErrors = getRefundMethodsErrors(refundMethodsSortable, languages)

    if (Object.keys(validationErrors).length > 0) {
      showErrors(validationErrors)

      return false
    }

    const configuration = getRefundMethodsConfiguration({
      refundMethods: refundMethodsSortable,
      issueRefundAtStatus,
      languages,
    })

    try {
      await saveConfiguration({ ...entityId, configuration })
    } catch (error) {
      const {
        errors: responseErrors,
        listErrors: responseListErrors,
        hasUnmappedErrors,
      } = getRefundMethodsErrorsFromResponse(error.response?.data, languages)
      const hasMappedErrors = Object.keys(responseErrors).length > 0 || responseListErrors.length > 0

      if (hasMappedErrors) showErrors(responseErrors, responseListErrors)
      if (!hasMappedErrors || hasUnmappedErrors) throw error

      return false
    }

    return true
  }

  useEffect(() => {
    if (!fieldIdToFocus) return

    document.getElementById(fieldIdToFocus)?.querySelector('input')?.focus()
    setFieldIdToFocus(null)
  }, [fieldIdToFocus])

  const toggleRefundMethodExpanded = refundMethodId => {
    setExpandedRefundMethodIds(ids => {
      return ids.includes(refundMethodId) ? ids.filter(id => id !== refundMethodId) : [...ids, refundMethodId]
    })
  }

  return {
    storePreferences: data,
    loadingStorePreferences: loading,
    refetchStorePreferences,
    savingConfiguration,
    hasChanges,
    revertChanges,
    issueRefundAtStatus,
    setIssueRefundAtStatus: params => {
      setHasChanges(true)
      setIssueRefundAtStatus(params)
    },
    refundMethodsSortable,
    savedRefundMethods,
    expandedRefundMethodIds,
    toggleRefundMethodExpanded,
    updateRefundMethod,
    saveRefundMethods,
    errors,
    listErrors,
    setRefundMethodsSortable: params => {
      setHasChanges(true)
      setListErrors([])
      setRefundMethodsSortable(params)
    },
    availableIssueRefundStatus: data?.available_issue_refund_status,
    loadingAllowedRefundMethods,
    refetchAllowedRefundMethods,
    dragging,
    setDragging,
    entityId,
    enabledPortalsWithGlobal: allowedRefundMethods?.enabled_portals,
  }
}

export default useRefundsConfiguration
